import React, { useState } from "react";
import api, { extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Eye, Loader2 } from "lucide-react";

export const ReportPresenze = ({ eventi }) => {
  const [mode, setMode] = useState("evento");
  const [eventoId, setEventoId] = useState("");
  const [da, setDa] = useState("");
  const [a, setA] = useState("");
  const [ordina, setOrdina] = useState("orario");
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState("");

  const buildParams = (formato) => {
    const params = { formato, ordina };
    if (mode === "evento") { params.evento_id = eventoId; }
    else { if (da) params.da = da; if (a) params.a = a; }
    return params;
  };

  const validate = () => {
    if (mode === "evento" && !eventoId) { toast.error("Seleziona un evento"); return false; }
    if (mode === "periodo" && !da && !a) { toast.error("Specifica un periodo"); return false; }
    return true;
  };

  const fetchBlob = async (formato) => {
    const res = await api.get("/accessi/report", { params: buildParams(formato), responseType: "blob" });
    return res.data;
  };

  const download = async (formato, ext) => {
    if (!validate()) return;
    setBusy(formato);
    try {
      const blob = await fetchBlob(formato);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `presenze.${ext}`; document.body.appendChild(link); link.click();
      link.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractError(e, "Errore nella generazione del report")); } finally { setBusy(""); }
  };

  const preview = async () => {
    if (!validate()) return;
    setBusy("preview");
    try {
      const blob = await fetchBlob("pdf");
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) { toast.error(extractError(e, "Errore nell'anteprima")); } finally { setBusy(""); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 min-w-0">
        <div className="font-display font-semibold text-slate-900 mb-3">Genera report presenze</div>
        <div className="flex gap-2 mb-4">
          <Button variant={mode === "evento" ? "default" : "outline"} size="sm" onClick={() => setMode("evento")} className={mode === "evento" ? "bg-[color:var(--brand-blue-600)] text-white" : ""} data-testid="report-mode-evento">Per evento</Button>
          <Button variant={mode === "periodo" ? "default" : "outline"} size="sm" onClick={() => setMode("periodo")} className={mode === "periodo" ? "bg-[color:var(--brand-blue-600)] text-white" : ""} data-testid="report-mode-periodo">Per periodo</Button>
        </div>

        {mode === "evento" ? (
          <div>
            <Label className="text-xs">Evento</Label>
            <Select value={eventoId || undefined} onValueChange={setEventoId}>
              <SelectTrigger data-testid="report-evento-select"><SelectValue placeholder="Seleziona un evento" /></SelectTrigger>
              <SelectContent>{eventi.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome_evento}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Dal</Label><Input type="date" value={da} onChange={(e) => setDa(e.target.value)} data-testid="report-da" /></div>
            <div><Label className="text-xs">Al</Label><Input type="date" value={a} onChange={(e) => setA(e.target.value)} data-testid="report-a" /></div>
          </div>
        )}

        <div className="mt-3">
          <Label className="text-xs">Ordinamento</Label>
          <Select value={ordina} onValueChange={setOrdina}>
            <SelectTrigger data-testid="report-ordina"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="orario">Cronologico (orario)</SelectItem>
              <SelectItem value="alfabetico">Alfabetico (cognome)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button onClick={preview} disabled={!!busy} variant="outline" data-testid="report-anteprima">
            {busy === "preview" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />} Anteprima
          </Button>
          <Button onClick={() => download("pdf", "pdf")} disabled={!!busy} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="report-pdf">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />} PDF
          </Button>
          <Button onClick={() => download("xlsx", "xlsx")} disabled={!!busy} variant="outline" data-testid="report-xlsx">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} Excel
          </Button>
          <Button onClick={() => download("csv", "csv")} disabled={!!busy} variant="outline" data-testid="report-csv">
            {busy === "csv" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} CSV
          </Button>
        </div>
      </Card>

      <Card className="p-3 min-w-0">
        <div className="text-xs text-muted-foreground mb-2 px-1">Anteprima PDF</div>
        {previewUrl ? (
          <iframe title="Anteprima report" src={previewUrl} className="w-full h-[70vh] rounded-lg border" data-testid="report-preview-frame" />
        ) : (
          <div className="h-[40vh] flex items-center justify-center text-sm text-muted-foreground border rounded-lg">Genera un'anteprima per visualizzare il report</div>
        )}
      </Card>
    </div>
  );
};
